import { z } from "zod/v4";
import { CreatedAt, UpdatedAt } from "../system";
import { ItemDistributionId, ItemDistributionIdSchema, ItemIdSchema } from "../../id";
import { DocumentSnapshotLike } from "../../firestore";
import { DocumentStatus, DocumentStatusHidden, TagsOptional, TagsOptionalForm } from "../../common"
import { Period, PeriodState, calculatePeriodState } from "../common"
import { ItemCommon, ItemDocumentMeta, ItemVariantLook, ItemVariantSpaceBackground, ItemVariantSpaceBGM } from "./item"
import { BooleanAllowString } from "../../lib/zods"

export const ItemDistributionCommon = z.object({
  name: z.string().nullable().describe("アイテムの名前。null の場合はアイテム側の名前を参照する。"),
  description: z.string().nullable().describe("商品の販売時説明文。null の場合はアイテム側の説明文を参照する。"),
  price: z.number().nullable().describe("価格。null の場合はアイテム側のデフォルト価格を参照する。"),
  model_number: z.string().nullable().describe("型番。同一のアイテムでも、いつ販売したものか（例: 初版）などを表すためのもの。"),
  is_charge_only: z.boolean().describe("有償コインのみを許容するかどうか。true の場合は、無償コインは利用できない。"),
  is_searchable: z.boolean().describe("検索回避フラグ。true の場合は、Algolia には同期されるが、false の場合は同期されないため、各種検索には表示されなくなる。"),
  is_new: z.boolean().describe("新着フラグ。true の場合は、販売開始から30日は新着フラグがつくようになるとのこと。"),
  is_recommended: z.boolean().nullable().describe("おすすめフラグ。true の場合は、おすすめ枠に表示されるようになるとのこと。検索可否フラグが false の場合、null になる（検索できないため）。"),
  recommend_priority: z.number().min(0).optional().describe("おすすめ優先度。おすすめフラグが true の場合のみ有効。値が小さいほど優先度が高い。"),
  is_reserved: z.boolean().describe("予約販売かどうかを示すフラグ。true の場合は予約販売。"),
  has_reserved_label: z.boolean().describe("予約販売ラベルの有無。再販のときには付けないことがあるため、人間によってコントロール可能。"),
  has_first_time_limited_label: z.boolean().nullable().describe("初回限定ラベルの有無。予約販売のときには null (指定不可)。"),
  is_period_limited: z.boolean().describe("販売期間の限定有無を示すフラグ。true の場合、end_at は null ではなくなる。"),
  has_period_limited_label: z.boolean().describe("期間限定ラベルの有無。"),
  is_stock_limited: z.boolean().describe("数量限定の有無を示すフラグ。true の場合、stock_quantity は null ではなくなる。"),
  has_stock_limited_label: z.boolean().describe("数量限定ラベルの有無。"),
  stock_quantity: z.number().nullable().describe("販売数量。null の場合は無制限。"),
  sold_quantity: z.number().describe("販売済み数量。"),
  start_at: z.number().describe("販売開始日時 (UNIX Timestamp mills) 。"),
  finish_at: z.number().nullable().describe("販売終了日時 (UNIX Timestamp mills) - 互換性のため残されています。end_at を使用してください。"),
  end_at: z.number().nullable().describe("販売終了日時 (UNIX Timestamp mills) 。"),
  sale_period: Period.describe("販売期間（start_at/end_atから自動生成）。Firestoreクエリ制限回避のため。"),
  sale_period_state: PeriodState.describe("販売期間の状態（システムが自動計算・管理）。Firestoreクエリ制限回避のため。注意: 一度 active=true になると、販売期間終了後も active=false には戻らない（永続公開）。期間終了時は next_change_at が null になり、デーモンの監視対象から外れる。"),
  published_at: z.number().nullable().describe("実際の公開日時。この値が入っているとき、Algoliaへの同期が行われている。null の場合は公開が終了している。"),
  available_start_at: z.number().nullable().describe("利用の開始日。この日付以降に利用可能になる。予約販売のときのみ値が入る。予約販売ではないときは null (常に利用可能)。"),
  status: DocumentStatus,
  tags: TagsOptional,
})

export const ItemDistributionReserved = z.object({
  is_reserved: z.literal(true),
  available_start_at: z.number().nullable(),
  has_reserved_label: z.boolean(),
  has_first_time_limited_label: z.literal(false),
})
export const ItemDistributionNonReserved = z.object({
  is_reserved: z.literal(false),
  available_start_at: z.number().nullable(),
  has_reserved_label: z.literal(false),
  has_first_time_limited_label: z.boolean(),
})
export const ItemDistributionIsReserved = z.discriminatedUnion("is_reserved", [
  ItemDistributionReserved,
  ItemDistributionNonReserved,
])

export const ItemDistributionLimited = z.object({
  is_period_limited: z.literal(true),
  has_period_limited_label: z.boolean(),
  finish_at: z.number(),
  end_at: z.number(),
})
export const ItemDistributionUnlimited = z.object({
  is_period_limited: z.literal(false),
  has_period_limited_label: z.literal(false),
  finish_at: z.null(),
  end_at: z.null(),
})
export const ItemDistributionIsLimited = z.discriminatedUnion("is_period_limited", [
  ItemDistributionLimited,
  ItemDistributionUnlimited,
])

export const ItemDistributionSearchableRecommended = z.object({
  is_searchable: z.literal(true),
  is_recommended: z.literal(true),
  recommend_priority: z.number().min(0).optional(),
  recommend_updated_at: z.number().min(0).optional(),
})
export const ItemDistributionSearchableNonRecommended = z.object({
  is_searchable: z.literal(true),
  is_recommended: z.literal(false),
})
export const ItemDistributionUnSearchable = z.object({
  is_searchable: z.literal(false),
  is_recommended: z.null(),
})
export const ItemDistributionIsSearchable = z.union([
  ItemDistributionSearchableRecommended,
  ItemDistributionSearchableNonRecommended,
  ItemDistributionUnSearchable,
])

export const ItemDistributionStockLimited = z.object({
  is_stock_limited: z.literal(true),
  has_stock_limited_label: z.boolean(),
  stock_quantity: z.number(),
})
export const ItemDistributionStockUnlimited = z.object({
  is_stock_limited: z.literal(false),
  has_stock_limited_label: z.literal(false),
  stock_quantity: z.null(),
})
export const ItemDistributionIsStockLimited = z.discriminatedUnion("is_stock_limited", [
  ItemDistributionStockLimited,
  ItemDistributionStockUnlimited,
])

export type ItemDistributionDocumentMeta = z.infer<typeof ItemDistributionDocumentMeta>
export const ItemDistributionDocumentMeta = z.intersection(
  ItemDistributionCommon,
  z.intersection(ItemDistributionIsReserved, z.intersection(ItemDistributionIsLimited, z.intersection(ItemDistributionIsSearchable, ItemDistributionIsStockLimited)))
)

/* eslint-disable popopo-msg/firestore-snake-case -- This is an API request schema (camelCase), not a Firestore schema (snake_case) */
const CreateItemDistributionRequestBase = z.object({
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  price: z.coerce.number().min(0).nullable().optional(),
  status: DocumentStatus,
  modelNumber: z.string().nullable().optional(),
  isChargeOnly: BooleanAllowString,
  isNew: BooleanAllowString,
  startAt: z.number(),
  finishAt: z.number().nullable().optional(),
  endAt: z.number().nullable().optional(),
  availableStartAt: z.number().nullable().optional(),
  isRecommended: BooleanAllowString.nullable(),
  isReserved: BooleanAllowString,
  hasReservedLabel: BooleanAllowString,
  hasFirstTimeLimitedLabel: BooleanAllowString.nullable(),
  isPeriodLimited: BooleanAllowString,
  hasPeriodLimitedLabel: BooleanAllowString,
  isSearchable: BooleanAllowString,
  isStockLimited: BooleanAllowString,
  hasStockLimitedLabel: BooleanAllowString,
  stockQuantity: z.coerce.number().min(1).max(99999).nullable().optional(),
})
/* eslint-enable popopo-msg/firestore-snake-case */

export type CreateItemDistributionRequest = z.infer<typeof CreateItemDistributionRequest>
export const CreateItemDistributionRequest = CreateItemDistributionRequestBase.extend({
  tags: TagsOptional,
}).refine((data) => {
  const endTime = data.endAt ?? data.finishAt
  if (endTime != null) {
    return data.startAt < endTime
  }
  return true
}, {
  message: "終了日時は開始日時より後である必要があります",
  path: ["endAt"],
}).refine((data) => {
  const endTime = data.endAt ?? data.finishAt
  if (data.availableStartAt != null && endTime != null) {
    return endTime <= data.availableStartAt
  }
  return true
}, {
  message: "利用開始日時は販売終了日時以降である必要があります",
  path: ["availableStartAt"],
})

export type CreateItemDistributionRequestForm = z.infer<typeof CreateItemDistributionRequestForm>
export const CreateItemDistributionRequestForm = CreateItemDistributionRequestBase.extend({
  tags: TagsOptionalForm,
}).refine((data) => {
  const endTime = data.endAt ?? data.finishAt
  if (endTime != null) {
    return data.startAt < endTime
  }
  return true
}, {
  message: "終了日時は開始日時より後である必要があります",
  path: ["endAt"],
}).refine((data) => {
  const endTime = data.endAt ?? data.finishAt
  if (data.availableStartAt != null && endTime != null) {
    return endTime <= data.availableStartAt
  }
  return true
}, {
  message: "利用開始日時は販売終了日時以降である必要があります",
  path: ["availableStartAt"],
})



export type UpdateItemDistributionRequest = z.infer<typeof UpdateItemDistributionRequest>
export const UpdateItemDistributionRequest = CreateItemDistributionRequest

export const ItemDistributionDocumentBody = z.intersection(
  ItemDistributionDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)
export type ItemDistributionDocumentBody = z.infer<typeof ItemDistributionDocumentBody>

export const ItemDistributionDocument = z.intersection(
  ItemDistributionDocumentBody,
  z.object({
    id: ItemDistributionIdSchema,
  })
)

export type ItemDistributionDocument = z.infer<typeof ItemDistributionDocument>

const ItemAlgoliaDenormalized = z.intersection(
  z.intersection(
    ItemCommon.pick({
      // name: true, // no need for algolia (see distribution)
      // description: true, // no need for algolia (see distribution)
      // brands: true, // converted to brand_ids, brand_names, brand_publishers
      default_price: true,
      media: true,
      // sales: false, // no need for algolia (see distribution)
      // status: true, // always "public"
      tags: true,
      // related_item_ids: true, // no need for algolia (see distribution)
    }),
    z.object({
      brand_ids: z.array(z.string()).describe("ブランドIDの配列"),
      brand_names: z.array(z.string()).describe("ブランド名の配列"),
      brand_publishers: z.array(z.string()).describe("版元の配列"),
      popular_score: z.number().optional().describe("人気スコア（Algoliaソート用、直近12時間の販売数）"),
    })
  ),
  z.union([
    // look
    ItemVariantLook.pick({
      kind: true,
      asset_bundle: true,
      media_account: true,
    }),

    // background
    ItemVariantSpaceBackground.pick({
      kind: true,
      atmosphere_text: true,
      capacity: true,
      recommend_bgm: true,
      asset_bundle: true,
      thumbnail_url: true,
      images: true,
      dress_codes: true,
    }),

    // BGM
    ItemVariantSpaceBGM.pick({
      kind: true,
      default_volume: true,
      trial_range: true,
      url: true,
    }),

  ])
).describe("Algolia向けに非正規化されたアイテムの情報。マスターデータの更新直後は古い情報が入っているので注意。")

export type ItemAlgoliaDenormalized = z.infer<typeof ItemAlgoliaDenormalized>

export const ItemDistributionAlgoliaDenormalized = z.intersection(
  ItemDistributionCommon,
  z.object({
    id: ItemIdSchema,
    item_distribution_id: ItemDistributionIdSchema,
    item: ItemAlgoliaDenormalized,
    related_items: z.array(ItemAlgoliaDenormalized),
    sale_price: z.number().describe("セール適用後の価格。セールがない場合は定価（price）と同じ値が入る。"),
    on_sale: z.boolean().describe("セール中かどうかを示すフラグ。"),
    sale_discount_rate: z.number().describe("割引率（パーセント）。セールがない場合は0。例: 20%オフの場合は20。"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
).describe("Algolia向けに非正規化されたアイテムの販売情報。マスターデータの更新直後は古い情報が入っているので注意。")

export type ItemDistributionAlgoliaDenormalized = z.infer<typeof ItemDistributionAlgoliaDenormalized>

export const ItemDistributionDenormalized = z.intersection(
  ItemDistributionCommon.pick({
    name: true,
    description: true,
    price: true,
    model_number: true,
    is_charge_only: true,
    is_reserved: true,
    has_reserved_label: true,
    has_first_time_limited_label: true,
    is_period_limited: true,
    has_period_limited_label: true,
    is_stock_limited: true,
    has_stock_limited_label: true,
    stock_quantity: true,
    sold_quantity: true,
    start_at: true,
    finish_at: true,
    end_at: true,
    available_start_at: true,
  }),
  z.object({
    id: ItemDistributionIdSchema,
  })
).describe("ユーザーインベントリ向けに非正規化されたアイテム配信情報。購入時のスナップショットを保持する。")

export type ItemDistributionDenormalized = z.infer<typeof ItemDistributionDenormalized>

export class ItemDistributionDocuments {

  /**
   * 販売期間内かどうかを判定する基本メソッド。
   * アイテムと配信情報のステータスが公開中で、かつ販売期間内であるかをチェックする。
   *
   * このメソッドは以下のメソッドから呼び出される共通ロジック:
   * - canBePurchased: このメソッド + 在庫チェック
   * - shouldBePublished: このメソッド + 検索可能フラグチェック
   *
   * @param item アイテム情報
   * @param distribution 配信情報
   * @param now 現在日時
   * @returns 販売期間内であれば true
   */
  private static isWithinSalePeriod(
    item: ItemDocumentMeta,
    distribution: ItemDistributionDocumentMeta,
    now: Date,
  ): boolean {
    const endTime = distribution.end_at ?? distribution.finish_at
    return item.status !== DocumentStatusHidden.value
      && distribution.status !== DocumentStatusHidden.value
      && distribution.start_at <= now.getTime()
      && (endTime == null || now.getTime() < endTime)
  }

  /**
   * 購入可能かどうかを判定する。
   * 販売期間内（isWithinSalePeriod）かつ在庫があるかをチェックする。
   *
   * 依存関係:
   * - isWithinSalePeriod を呼び出す
   *
   * 在庫チェックを含むため、売り切れ商品は購入不可となる。
   * 公開判定（shouldBePublished）は在庫チェックを含まないため、
   * 売り切れ商品もAlgolia/Firestoreで公開状態として扱われる。
   *
   * @param item アイテム情報
   * @param distribution 配信情報
   * @param now 現在日時
   * @returns 購入可能であれば true
   */
  static canBePurchased(
    item: ItemDocumentMeta,
    distribution: ItemDistributionDocumentMeta,
    now: Date,
  ): boolean {
    const hasStock = !distribution.is_stock_limited || distribution.sold_quantity < distribution.stock_quantity
    return this.isWithinSalePeriod(item, distribution, now)
      && hasStock
  }

  static isOutOfStock(distribution: ItemDistributionDocumentMeta): boolean {
    return distribution.is_stock_limited && distribution.stock_quantity <= distribution.sold_quantity
  }

  static getRemainingStock(distribution: ItemDistributionDocumentMeta): number | null {
    if (!distribution.is_stock_limited) {
      return null
    }
    return Math.max(0, distribution.stock_quantity - distribution.sold_quantity)
  }

  /**
   * 公開すべきかどうかを判定する。
   * 販売期間内（isWithinSalePeriod）かつ検索可能フラグが有効かをチェックする。
   *
   * 依存関係:
   * - isWithinSalePeriod を呼び出す
   *
   * 重要: このメソッドは在庫チェックを含まない。
   * 売り切れ状態をユーザーに表示するため、在庫が0でもAlgolia/Firestoreで公開状態として扱う。
   * クライアント側では stock_quantity, sold_quantity, is_stock_limited フィールドを
   * 使用して売り切れ状態を判定・表示する。
   * 購入可否の判定には canBePurchased を使用すること。
   *
   * @param item アイテム情報
   * @param distribution 配信情報
   * @param now 現在日時
   * @returns 公開すべきであれば true
   */
  static shouldBePublished(
    item: ItemDocumentMeta,
    distribution: ItemDistributionDocumentMeta,
    now: Date,
  ): boolean {
    return this.isWithinSalePeriod(item, distribution, now)
      && distribution.is_searchable !== false
  }

  static toItemDistributionDocumentMeta(
    request: CreateItemDistributionRequest | UpdateItemDistributionRequest,
    published_at: number | null
  ): ItemDistributionDocumentMeta {
    const baseData = {
      name: request.name ?? null,
      description: request.description ?? null,
      price: request.price ?? null,
      model_number: request.modelNumber ?? null,
      is_charge_only: request.isChargeOnly,
      is_new: request.isNew,
      start_at: request.startAt,
      available_start_at: request.availableStartAt ?? null,
      status: request.status,
      tags: request.tags?.filter(tag => tag.trim()) ?? [],
    }

    const searchableData = request.isSearchable
      ? request.isRecommended
        ? {
          is_searchable: true as const,
          is_recommended: true as const,
          recommend_priority: undefined,
        }
        : {
          is_searchable: true as const,
          is_recommended: false as const,
        }
      : {
        is_searchable: false as const,
        is_recommended: null,
      }

    const reservedData = request.isReserved
      ? {
        is_reserved: true as const,
        has_reserved_label: request.hasReservedLabel,
        has_first_time_limited_label: false as const,
      }
      : {
        is_reserved: false as const,
        has_reserved_label: false as const,
        has_first_time_limited_label: request.hasFirstTimeLimitedLabel ?? false,
      }

    const endTime = request.endAt ?? request.finishAt
    const periodLimitedData = request.isPeriodLimited
      ? {
        is_period_limited: true as const,
        finish_at: endTime ?? Date.now() + 86400000,
        end_at: endTime ?? Date.now() + 86400000,
        has_period_limited_label: request.hasPeriodLimitedLabel
      }
      : {
        is_period_limited: false as const,
        finish_at: null,
        end_at: null,
        has_period_limited_label: false as const
      }

    const stockLimitedData = request.isStockLimited
      ? {
        is_stock_limited: true as const,
        has_stock_limited_label: request.hasStockLimitedLabel,
        stock_quantity: request.stockQuantity ?? 1,
      }
      : {
        is_stock_limited: false as const,
        has_stock_limited_label: false as const,
        stock_quantity: null,
      }

    const sale_period: Period = {
      start_at: request.startAt,
      end_at: endTime ?? null
    }
    const sale_period_state = calculatePeriodState(sale_period, Date.now())

    return {
      ...baseData,
      ...searchableData,
      ...reservedData,
      ...periodLimitedData,
      ...stockLimitedData,
      sold_quantity: 0,
      sale_period,
      sale_period_state,
      published_at,
    }
  }
  static toDocumentData(doc: ItemDistributionDocument): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    };
  }

  static toDenormalized(distribution: ItemDistributionDocument): ItemDistributionDenormalized {
    return {
      id: distribution.id,
      name: distribution.name,
      description: distribution.description,
      price: distribution.price,
      model_number: distribution.model_number,
      is_charge_only: distribution.is_charge_only,
      is_reserved: distribution.is_reserved,
      has_reserved_label: distribution.has_reserved_label,
      has_first_time_limited_label: distribution.has_first_time_limited_label,
      is_period_limited: distribution.is_period_limited,
      has_period_limited_label: distribution.has_period_limited_label,
      is_stock_limited: distribution.is_stock_limited,
      has_stock_limited_label: distribution.has_stock_limited_label,
      stock_quantity: distribution.stock_quantity,
      sold_quantity: distribution.sold_quantity,
      start_at: distribution.start_at,
      finish_at: distribution.finish_at,
      end_at: distribution.end_at,
      available_start_at: distribution.available_start_at,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ItemDistributionDocument | null {
    if (!doc) {
      return null;
    }
    const data = doc.data();
    if (data === undefined) {
      return null;
    }

    return {
      id: ItemDistributionId(doc.id),
      name: data["name"] ?? null,
      description: data["description"] ?? null,
      price: data["price"] ?? null,
      model_number: data["model_number"],
      is_charge_only: data["is_charge_only"],
      is_searchable: data["is_searchable"],
      is_new: data["is_new"],
      is_recommended: data["is_recommended"],
      recommend_priority: data["recommend_priority"],
      recommend_updated_at: data["recommend_updated_at"],
      is_reserved: data["is_reserved"],
      has_reserved_label: data["has_reserved_label"],
      has_first_time_limited_label: data["has_first_time_limited_label"],
      is_period_limited: data["is_period_limited"],
      has_period_limited_label: data["has_period_limited_label"],
      is_stock_limited: data["is_stock_limited"] ?? false,
      has_stock_limited_label: data["has_stock_limited_label"] ?? false,
      stock_quantity: data["stock_quantity"] ?? null,
      sold_quantity: data["sold_quantity"] ?? 0,
      start_at: data["start_at"],
      finish_at: data["finish_at"],
      end_at: data["end_at"],
      sale_period: data["sale_period"] ?? { start_at: data["start_at"], end_at: data["end_at"] ?? null },
      sale_period_state: data["sale_period_state"] ?? calculatePeriodState({ start_at: data["start_at"], end_at: data["end_at"] ?? null }, Date.now()),
      published_at: data["published_at"],
      available_start_at: data["available_start_at"],
      status: data["status"],
      tags: data["tags"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * ショップ検索用おすすめアイテムの優先度更新リクエスト（個別アイテム）
 */
export const UpdateShopSearchRecommendedItemRequest = z.object({
  itemId: ItemIdSchema,
  itemDistributionId: ItemDistributionIdSchema,
  recommendPriority: z.number().min(0).nullable(),
  previousUpdatedAt: z.number().min(0).optional().describe("更新衝突検知用。recommend_updated_at の値を指定する。"),
})
export type UpdateShopSearchRecommendedItemRequest = z.infer<typeof UpdateShopSearchRecommendedItemRequest>

/**
 * ショップ検索用おすすめアイテムの優先度更新リクエスト（一括）
 */
export const UpdateShopSearchRecommendedItemsRequest = z.object({
  items: z.array(UpdateShopSearchRecommendedItemRequest),
})
export type UpdateShopSearchRecommendedItemsRequest = z.infer<typeof UpdateShopSearchRecommendedItemsRequest>
